import { ref, computed } from 'vue';

/**
 * MyTasks (v3 homepage TaskList) — core logic, shared by both portals.
 *
 * Owns the grouped-inbox behaviour for the user's open workflow tasks:
 * filtering (doc-type + search), grouping (Due soon / Later / Waiting on
 * others), per-group collapse + "view all" expansion, and the per-row
 * status/icon/action/due presentation derivations.
 *
 * Framework-agnostic, `self` pattern (see scorePanel.js). Reactive inputs are
 * injected as GETTERS so the composable never imports a store — beta passes
 * Pinia refs, legacy passes Vuex getters. Navigation (opening a task) stays in
 * the presentation layer because the router differs per portal.
 *
 * Collapse/expand state is held as a `ref({})` reassigned on change (rather than
 * a `reactive` map) so new keys are reactive in BOTH Vue 2.7 and Vue 3 — Vue
 * 2.7's reactivity can't observe a new key added to a reactive object.
 *
 * @param {Object} options
 * @param {() => any[]}   options.getTasks    current task list
 * @param {() => boolean} [options.getLoading] tasks loading flag
 * @param {() => number}  [options.getLimit]   rows per group before "view all"
 * @param {() => string[]}[options.getDocTypes] doc-type include filter
 */
export function taskList(options) {
  const {
    getTasks = () => [],
    getLoading = () => false,
    getLimit = () => 5,
    getDocTypes = () => [],
  } = options || {};

  const limit = () => getLimit() || 5;

  const self = {
    groupBy: ref('due'),
    q: ref(''),
    searchOpen: ref(false),
    collapsed: ref({}),
    expanded: ref({}),
  };

  self.isLoading = computed(() => !!getLoading());
  self.taskCount = computed(() => (getTasks() || []).length);

  self.filtered = computed(() => {
    let list = getTasks() || [];
    const docTypes = getDocTypes() || [];
    if (docTypes.length) {
      const want = docTypes.map((d) => String(d).toLowerCase());
      list = list.filter((t) => want.includes(String(t.doc_type || '').toLowerCase()));
    }
    const ql = self.q.value.trim().toLowerCase();
    if (ql) {
      list = list.filter((t) => `${t.doc_name || ''} ${t.reference || ''}`.toLowerCase().includes(ql));
    }
    return list;
  });

  // --- due / waiting classification ---
  self.dueDays = (t) => {
    if (!t.due_date) return null;
    const d = new Date(t.due_date);
    if (isNaN(d.getTime())) return null;
    return Math.floor((d.getTime() - Date.now()) / 86400000);
  };
  self.isDueSoon = (t) => {
    const d = self.dueDays(t);
    return d !== null && d <= 7;
  };
  // The actionable state comes from the doc's LIFECYCLE status name (client-
  // configured) — `task_status` is only Open/Completed/Cancelled/Unassigned and
  // carries no in-progress/returned/waiting semantics. Heuristic name match
  // (a per-tenant status→state map would make it exact); falls back to task_status.
  self.statusText = (t) => String((t && (t.lifecycle_status_name || t.doc_status_text || t.task_status)) || '');
  self.isWaiting = (t) => /wait|await|pending|review|approv|with /i.test(self.statusText(t));

  self.waiting = computed(() => self.filtered.value.filter((t) => self.isWaiting(t)));
  self.soon = computed(() => self.filtered.value.filter((t) => !self.isWaiting(t) && self.isDueSoon(t)));
  self.later = computed(() => self.filtered.value.filter((t) => !self.isWaiting(t) && !self.isDueSoon(t)));

  self.displayGroups = computed(() => {
    if (self.groupBy.value === 'none') {
      return [{ id: 'all', label: '', items: self.filtered.value, noHead: true }];
    }
    return [
      { id: 'soon', label: 'Due soon', items: self.soon.value, noHead: false },
      { id: 'later', label: 'Later', items: self.later.value, noHead: false },
      { id: 'waiting', label: 'Waiting on others', items: self.waiting.value, noHead: false },
    ].filter((g) => g.items.length);
  });

  // Collapse defaults scale with load: Waiting collapses past 15, Later past 40.
  self.defaultCollapsed = (id) => {
    const total = self.filtered.value.length;
    if (id === 'waiting') return total >= 15;
    if (id === 'later') return total >= 40;
    return false;
  };
  self.isCollapsed = (g) => {
    const v = self.collapsed.value[g.id];
    return v === undefined ? self.defaultCollapsed(g.id) : v;
  };
  self.toggle = (id, expandOnly = false) => {
    const next = expandOnly ? false : !self.isCollapsed({ id });
    self.collapsed.value = { ...self.collapsed.value, [id]: next };
  };
  self.toggleExpand = (id) => {
    self.expanded.value = { ...self.expanded.value, [id]: !self.expanded.value[id] };
  };
  self.shown = (g) => {
    if (self.isCollapsed(g)) return [];
    return self.expanded.value[g.id] ? g.items : g.items.slice(0, limit());
  };
  self.isExpanded = (id) => !!self.expanded.value[id];

  // --- row presentation ---
  self.statusKind = (t) => {
    const s = self.statusText(t).toLowerCase();
    if (/return|sent back|revis|reject|bounce/.test(s)) return 'warn';
    if (/progress|draft|started|working/.test(s)) return 'accent';
    if (self.isWaiting(t)) return 'wait';
    return 'ready';
  };
  self.statusIcon = (t) => {
    const k = self.statusKind(t);
    return k === 'warn' ? 'undo' : k === 'accent' ? 'pending' : k === 'wait' ? 'hourglass_top' : 'radio_button_unchecked';
  };
  // Status indicator (prototype V3StatusIc): a circular PROGRESS arc for
  // in-progress, an hourglass for waiting-on-others, else a dashed "ready" ring
  // (NOT a filled radio). The widget renders the SVG; these drive which + the arc.
  self.STIC_CIRC = 2 * Math.PI * 7; // r=7
  self.statusVisual = (t) => {
    const k = self.statusKind(t);
    if (k === 'accent') return 'progress';
    if (k === 'wait') return 'waiting';
    return 'dashed';
  };
  self.stepFrac = (t) => {
    const steps = Number(t.steps) || 0;
    const step = Number(t.step) || 0;
    if (steps > 0) return Math.max(0, Math.min(1, step / steps));
    return 0.4; // no step data on tasklist rows → a generic "in progress" arc
  };
  self.stepText = (t) => {
    const steps = Number(t.steps) || 0;
    return steps ? `Step ${Number(t.step) || 0} of ${steps}` : '';
  };
  // MyTasks surfaces one hover-revealed CTA: "Start" for an unworked task,
  // "Continue" once in progress, "Revise" when sent back, "View" when waiting.
  self.actionLabel = (t) => {
    const s = self.statusText(t).toLowerCase();
    if (/return|sent back|revis|reject|bounce/.test(s)) return 'Revise';
    if (/progress|draft|started|working/.test(s)) return 'Continue';
    if (self.isWaiting(t)) return 'View';
    return 'Start';
  };
  self.dueText = (t) => {
    if (!t.due_date) return '';
    const d = new Date(t.due_date);
    if (isNaN(d.getTime())) return String(t.due_date);
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
    return hasTime
      ? `${date}, ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
      : date;
  };
  self.dueClass = (t) => {
    const d = self.dueDays(t);
    if (d === null) return '';
    if (d < 0) return 'v3-due--over';
    if (d <= 7) return 'v3-due--soon';
    return '';
  };
  self.rowSub = (t) => t.document_status || t.doc_status_text || t.lifecycle_status_name || '';

  return self;
}
